import { Tag } from '@coop/components';
import * as React from 'react';

import useReactRouting from '../../../ecommerce/hooks/useReactRouting';
import { ga4RecipesTracking } from '../../tracking/ga4/recipes';
import BaseRecipeTeaser from '../BaseRecipeTeaser';
import FavoriteRecipeToggle from '../molecules/FavoriteRecipeToggle';
import { RatingStars } from '../molecules/RatingStars';
import { RecipeButtonWithFlyIn, useRecipeModal } from '../RecipeModalTrigger';

interface BuyableRecipeTeaserProps {
    id: string;
    url: string;
    name: string;
    rating: number;
    votes: number;
    imageUrl: string;
    co2ImpactKg: number | null;
    defaultPortions: number;
    onBuyClick?: (e: React.SyntheticEvent) => void;
    onLinkClick?: (e: React.SyntheticEvent) => void;
    analyticsListName: string;
    withBorder?: boolean;
}

const BuyableRecipeTeaser = React.forwardRef<HTMLDivElement, BuyableRecipeTeaserProps>(
    (props, ref) => {
        const { tryOpen, isOpen, close } = useRecipeModal(props.id, props.name);

        const { push } = useReactRouting();

        const track = () => {
            ga4RecipesTracking.click.buyableRecipe(props.id, props.name);
        };

        const handleImageClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            track();
            push(props.url, true);
            props.onLinkClick?.(e);
        };

        const handleLinkClick = (e: React.SyntheticEvent) => {
            track();
            props.onLinkClick?.(e);
        };

        const handleBuyClick = (e: React.SyntheticEvent) => {
            tryOpen();
            track();
            props.onBuyClick?.(e);
        };

        return (
            <BaseRecipeTeaser.Wrapper
                ref={ref}
                name={props.name}
                imageUrl={props.imageUrl}
                withBorder={props.withBorder}
                topSlot={
                    <FavoriteRecipeToggle
                        recipeName={props.name}
                        recipeId={props.id}
                        type="recipe"
                    />
                }
                clickableImage
                onImageClick={handleImageClick}
            >
                <BaseRecipeTeaser.Badges>
                    <Tag type="read-only" theme="neutral">
                        Recept
                    </Tag>
                </BaseRecipeTeaser.Badges>
                <BaseRecipeTeaser.Content>
                    <BaseRecipeTeaser.Info>
                        <BaseRecipeTeaser.Heading
                            as="a"
                            href={props.url}
                            flexOrder={1}
                            onClick={handleLinkClick}
                        >
                            {props.name}
                        </BaseRecipeTeaser.Heading>
                        <BaseRecipeTeaser.RatingContainer>
                            <RatingStars rating={props.rating} votes={props.votes} />
                            <BaseRecipeTeaser.ClimateImpact co2ImpactKg={props.co2ImpactKg} />
                        </BaseRecipeTeaser.RatingContainer>
                    </BaseRecipeTeaser.Info>
                    <BaseRecipeTeaser.Actions>
                        <RecipeButtonWithFlyIn
                            viewType="both"
                            allowRecipePageRedirect
                            recipeId={props.id}
                            recipeName={props.name}
                            recipeType="recipe"
                            defaultPortions={props.defaultPortions}
                            buttonSize="default"
                            onBuyClick={handleBuyClick}
                            onRemoveClick={(e) => e.stopPropagation()}
                            buttonFullWidth
                            analyticsListName={props.analyticsListName}
                            isOpen={isOpen}
                            close={close}
                        />
                    </BaseRecipeTeaser.Actions>
                </BaseRecipeTeaser.Content>
            </BaseRecipeTeaser.Wrapper>
        );
    },
);

export default BuyableRecipeTeaser;
